/** @format */

import { useNavigate } from '@fukuri-admins/hooks/common';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getClientAdminUser, updateClientAdminUser } from '../../../api/client_admin_users/id';
import { ActionButton } from '../../../components/common/ActionButton';
import { ActionInput } from '../../../components/common/ActionInput';
import { ContentsHeader } from '../../../components/common/ContentsHeader';
import MessageModal from '../../../components/common/MessageModal';
import { EditClientAdminUserFormType } from '../../../types/forms/EditClientAdminUserFormType';
import { getClientAdminUsersLink } from '../../../utils/getLinks';
import {
  validateEmail,
  validatePassword,
  validatePasswordConfirmation,
  validateRequired,
} from '../../../utils/validation';
import styles from './styles.module.scss';

export const EditClientAdminUser = () => {
  const navigate = useNavigate();
  const { client_admin_id } = useParams();
  const [clientAdminUser, setClientAdminUser] = useState<EditClientAdminUserFormType>();
  const [startClientsAdminUpdateStatus, setStartClientsAdminUpdateStatus] = useState(false);
  const [errMsg, setErrMsg] = useState<string[]>([]);
  const [apiErrMsg, setApiErrMsg] = useState('');
  const [editClientAdminUserFinishStatus, setEditClientFinishStatus] = useState(false);
  const [errorMessageModalStatus, setErrorMessageModalStatus] = useState(false);
  const [apiErrorMessageModalStatus, setApiErrorMessageModalStatus] = useState(false);
  const handlePreviousRoute = () => {
    navigate(getClientAdminUsersLink());
  };
  const { showToast } = useToast();

  const validateEditClientAdminUserForm = (data: EditClientAdminUserFormType): string[] => {
    const errors: string[] = [];

    const validations = [validateRequired(data.full_name, 'フルネーム'), validateEmail(data.email)];

    if (data.password && data.password.length > 0) {
      validations.push(
        validatePassword(data.password),
        validatePasswordConfirmation(data.password, data.password_confirmation)
      );
    }

    validations.forEach(result => {
      if (result) {
        errors.push(result);
      }
    });

    return errors;
  };

  const handleNext = () => {
    setStartClientsAdminUpdateStatus(true);

    const inputProps: EditClientAdminUserFormType = {
      full_name: clientAdminUser.full_name,
      email: clientAdminUser.email,
      password: clientAdminUser.password,
      password_confirmation: clientAdminUser.password_confirmation,
    };

    const errors = validateEditClientAdminUserForm(inputProps);

    if (errors.length > 0) {
      setErrMsg(errors);
      setErrorMessageModalStatus(true);
      return;
    }

    usersUpdate(inputProps);
  };

  const usersUpdate = async (e: EditClientAdminUserFormType) => {
    const obj = {
      full_name: e.full_name,
      email: e.email,
      password: e.password || null,
      password_confirmation: e.password_confirmation || null,
    };

    try {
      await updateClientAdminUser({ client_admin_user: obj }, client_admin_id);
      setApiErrMsg('');
      setEditClientFinishStatus(true);
    } catch (error) {
      setApiErrMsg(
        `Error updating user:\n${error.message || 'Unknown error'}\nDetails: ${
          error.details || 'No additional details'
        }`
      );
      setApiErrorMessageModalStatus(true);
    }
  };

  const handleEditUserFinish = () => {
    setEditClientFinishStatus(false);
    navigate(getClientAdminUsersLink());
  };

  const handleErrorMessageModal = () => {
    setErrorMessageModalStatus(false);
  };

  const handleErrorMessage2Modal = () => {
    setApiErrorMessageModalStatus(false);
  };

  const init = async () => {
    if (!client_admin_id) return;
    try {
      const data = await getClientAdminUser(client_admin_id);
      setClientAdminUser(data.client_admin_user);
    } catch {
      showToast({ message: 'データの取得に失敗しました。', status: 'danger' });
    }
  };

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    if (startClientsAdminUpdateStatus && !editClientAdminUserFinishStatus) {
      navigate(getClientAdminUsersLink());
    }
  }, [editClientAdminUserFinishStatus]);

  return (
    <>
      <div className={styles.root}>
        <ContentsHeader title='利用企業管理者編集' />
        <div className={styles.container}>
          <div className={styles.mainContainer} data-testid='edit-client-admin-form'>
            <div className={styles.errorMessage} data-testid='edit-error-messages'>
              {errMsg.length > 0 ? (
                <>
                  <div>入力エラー</div>
                  <ul>
                    {errMsg.map((item, key: number) => {
                      return <li key={key}>{item}</li>;
                    })}
                  </ul>
                </>
              ) : null}
            </div>
            <div className={styles.form}>
              <div>
                <div className={styles.formTitle}>
                  フルネーム <span className={styles.symbol}>*</span>
                </div>
                <ActionInput
                  data-testid='full-name-input'
                  defaultValue={clientAdminUser?.full_name ? clientAdminUser?.full_name : ''}
                  action={e =>
                    setClientAdminUser({
                      ...clientAdminUser,
                      full_name: e.target.value,
                    })
                  }
                  placeholder='山田'
                />
              </div>
            </div>
            <div className={styles.form}>
              <div className={styles.formTitle}>
                メールアドレス <span className={styles.symbol}>*</span>
              </div>
              <ActionInput
                data-testid='email-input'
                defaultValue={clientAdminUser?.email ? clientAdminUser?.email : ''}
                action={e =>
                  setClientAdminUser({
                    ...clientAdminUser,
                    email: e.target.value,
                  })
                }
                placeholder='xxx@xxxx.xx.xx'
                className={styles.formMiddle}
              />
            </div>
            <div className={styles.form}>
              <div className={styles.formTitle}>
                パスワード<span className={styles.kome}>（※1）</span>
              </div>
              <ActionInput
                data-testid='password-input'
                type='password'
                defaultValue={clientAdminUser?.password ? clientAdminUser?.password : ''}
                action={e =>
                  setClientAdminUser({
                    ...clientAdminUser,
                    password: e.target.value,
                  })
                }
                placeholder='xxxxxxxx'
                className={styles.formShort}
                autoComplete='none'
              />
            </div>
            <div className={styles.form}>
              <div className={styles.formTitle}>
                パスワード（再入力）<span className={styles.kome}>（※1）</span>
              </div>
              <ActionInput
                data-testid='password-confirm-input'
                type='password'
                defaultValue={
                  clientAdminUser?.password_confirmation
                    ? clientAdminUser?.password_confirmation
                    : ''
                }
                action={e =>
                  setClientAdminUser({
                    ...clientAdminUser,
                    password_confirmation: e.target.value,
                  })
                }
                placeholder='xxxxxxxx'
                className={styles.formShort}
                autoComplete='none'
              />
            </div>
          </div>
          <div className={styles.btnContainer}>
            <ActionButton
              data-testid='cancel-button'
              content='キャンセル'
              className={styles.cancelBtn}
              action={handlePreviousRoute}
            />
            <ActionButton
              data-testid='submit-button'
              content='登録'
              type='dark'
              className={styles.confirmBtn}
              action={() => handleNext()}
            />
          </div>
          <MessageModal
            data-testid='success-modal'
            title='利用企業管理者情報修正完了'
            description='利用企業管理者情報を修正しました。'
            show={editClientAdminUserFinishStatus}
            onClose={() => setEditClientFinishStatus(false)}
            action={handleEditUserFinish}
          />
          <MessageModal
            data-testid='validation-modal'
            title='入力エラー'
            description='入力エラーがあります。画面上部に表示しているエラーメッセージをご確認ください。'
            show={errorMessageModalStatus}
            onClose={() => setErrorMessageModalStatus(false)}
            action={handleErrorMessageModal}
          />
          <MessageModal
            data-testid='error-modal'
            title='利用企業管理者情報修正エラー'
            description={`利用企業管理者情報修正エラーが発生しました。\n\n<<エラーメッセージ>>\n${apiErrMsg}`}
            show={apiErrorMessageModalStatus}
            onClose={() => setApiErrorMessageModalStatus(false)}
            action={handleErrorMessage2Modal}
          />
        </div>
      </div>
    </>
  );
};
